import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "../../layouts/Navbar";
import Footer from "../../layouts/Footer";
import Sidebar from "../../layouts/Sidebar";
import axios from "axios";
import { toast } from "react-toastify";

const EditCategory = () => {
  const { catId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ 
    title: "", 
    year: new Date().getFullYear().toString() 
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [categoryDetails, setCategoryDetails] = useState(null);

  // Generate year options from 2000 to current year + 1
  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: currentYear - 2000 + 2 },
    (_, i) => (currentYear + 1 - i).toString()
  );

  const fetchCategory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `https://pastq.eddcodesgh.com/qApi/admin/categories/single_category.php?catId=${catId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization:
              "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506",
          },
          withCredentials: true,
        }
      );

      if (response.data.status === 200 && response.data.records[0]) {
        const category = response.data.records[0];
        setFormData({
          title: category.cat_title || "",
          year: category.year || currentYear.toString(),
        });
        setCategoryDetails(category);
      } else {
        toast.error("Category not found", {
          position: "top-right",
          autoClose: 3000,
        });
        // navigate("/admin/categories");
      }
    } catch (error) {
      toast.error("Failed to load category details", {
        position: "top-right",
        autoClose: 3000,
      });
      
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (catId) {
      fetchCategory();
    }
  }, [catId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) {
      newErrors.title = "Category title is required";
      toast.error("Category title is required", {
        position: "top-right",
        autoClose: 3000,
      });
    }
    if (!formData.year) {
      newErrors.year = "Year is required";
      toast.error("Please select a year", {
        position: "top-right",
        autoClose: 3000,
      });
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      const response = await axios.put(
        `https://pastq.eddcodesgh.com/qApi/admin/categories/updatecategory.php?catId=${catId}`,
        formData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization:
              "Bearer f0ed28e67f2a90c8bf437f1ff949ef23655e842a2901b7b87779673a0d176506",
          },
          withCredentials: true,
        }
      );

      if (response.data.status === 200) {
        toast.success("Category updated successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
        // Refresh category details
        fetchCategory();
      } else {
        throw new Error(response.data.message || "Update failed");
      }
    } catch (error) {
      toast.error(error.message || "An error occurred while updating category", {
        position: "top-right",
        autoClose: 4000,
      });
      setErrors((prev) => ({
        ...prev,
        submit: error.message || "An error occurred",
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin/categories");
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <Sidebar />
        <main id="main" className="main">
          <div className="pagetitle">
            <h1>Edit Category</h1>
            <nav>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/admin/categories">Categories</Link>
                </li>
                <li className="breadcrumb-item active">Loading...</li>
              </ol>
            </nav>
          </div>
          <section className="section">
            <div className="row justify-content-center">
              <div className="col-lg-8">
                <div className="card">
                  <div className="card-body text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3">Loading category details...</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <Sidebar />
      
      <main id="main" className="main">
        <div className="pagetitle">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1>Edit Category</h1>
              <nav>
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/dashboard">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/admin/categories">Categories</Link>
                  </li>
                  <li className="breadcrumb-item active">
                    Edit: {categoryDetails?.cat_title}
                  </li>
                </ol>
              </nav>
            </div>
            <Link to="/admin/categories" className="btn btn-outline-primary">
              <i className="bi bi-arrow-left me-2"></i>
              Back to Categories
            </Link>
          </div>
        </div>

        <section className="section">
          <div className="row">
            {/* Left Column - Edit Form */}
            <div className="col-lg-8">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="card-title mb-0">Edit Category Details</h5>
                    <div className="badge bg-info">
                      <i className="bi bi-pencil-square me-1"></i>
                      Editing
                    </div>
                  </div>

                  <form onSubmit={handleSubmit}>
                    {errors.submit && (
                      <div className="alert alert-danger alert-dismissible fade show" role="alert">
                        {errors.submit}
                        <button type="button" className="btn-close" onClick={() => setErrors({})}></button>
                      </div>
                    )}

                    <div className="row mb-3">
                      <div className="col-md-8">
                        <label htmlFor="title" className="form-label">
                          Category Title <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          id="title"
                          name="title"
                          className={`form-control ${errors.title ? "is-invalid" : ""}`}
                          placeholder="e.g., Mathematics, English Language, Biology"
                          value={formData.title}
                          onChange={handleChange}
                          required
                        />
                        {errors.title && (
                          <div className="invalid-feedback">{errors.title}</div>
                        )}
                        <div className="form-text">
                          Update the category title as needed
                        </div>
                      </div>
                      
                      <div className="col-md-4">
                        <label htmlFor="year" className="form-label">
                          Year <span className="text-danger">*</span>
                        </label>
                        <select
                          id="year"
                          name="year"
                          className={`form-select ${errors.year ? "is-invalid" : ""}`}
                          value={formData.year}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select Year</option>
                          {years.map((year) => (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          ))}
                        </select>
                        {errors.year && (
                          <div className="invalid-feedback">{errors.year}</div>
                        )}
                        <div className="form-text">
                          Update the academic year
                        </div>
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-12">
                        <div className="alert alert-info">
                          <i className="bi bi-info-circle me-2"></i>
                          <strong>Note:</strong> Updating this category will affect all related past questions.
                        </div>
                      </div>
                    </div>

                    <div className="d-flex gap-2 mt-4">
                      <button
                        className="btn btn-primary px-4"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Updating...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-check-circle me-2"></i>
                            Update Category
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary px-4"
                        onClick={handleCancel}
                        disabled={isSubmitting}
                      >
                        <i className="bi bi-x-circle me-2"></i>
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            {/* Right Column - Category Information */}
            <div className="col-lg-4">
              <div className="card">
                <div className="card-body">
                  <h5 className="card-title">Category Information</h5>
                  
                  <div className="mb-4">
                    <div className="d-flex align-items-center mb-3">
                      <div className="bg-primary bg-opacity-10 rounded-3 p-3 me-3">
                        <i className="bi bi-folder text-primary fs-4"></i>
                      </div>
                      <div>
                        <h6 className="mb-1">Current Details</h6>
                        <small className="text-muted">Last updated information</small>
                      </div>
                    </div>
                    
                    <div className="list-group list-group-flush">
                      <div className="list-group-item d-flex justify-content-between align-items-center border-0 px-0 py-2">
                        <span>Category ID:</span>
                        <span className="fw-bold">{catId}</span>
                      </div>
                      <div className="list-group-item d-flex justify-content-between align-items-center border-0 px-0 py-2">
                        <span>Original Title:</span>
                        <span className="fw-bold text-truncate" style={{ maxWidth: "150px" }}>
                          {categoryDetails?.cat_title}
                        </span>
                      </div>
                      <div className="list-group-item d-flex justify-content-between align-items-center border-0 px-0 py-2">
                        <span>Original Year:</span>
                        <span className="fw-bold">{categoryDetails?.year || "N/A"}</span>
                      </div>
                      <div className="list-group-item d-flex justify-content-between align-items-center border-0 px-0 py-2">
                        <span>Status:</span>
                        <span className="badge bg-success">Active</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-top">
                    <h6 className="text-muted mb-3">Quick Actions</h6>
                    <div className="d-grid gap-2">
                      <Link 
                        to={`/admin/categories/${catId}/questions`} 
                        className="btn btn-outline-primary btn-sm"
                      >
                        <i className="bi bi-journal-text me-2"></i>
                        View Questions
                      </Link>
                      <Link 
                        to={`/admin/categories/${catId}/add-question`} 
                        className="btn btn-outline-success btn-sm"
                      >
                        <i className="bi bi-plus-circle me-2"></i>
                        Add Question
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* Change History Card */}
              <div className="card mt-3">
                <div className="card-body">
                  <h6 className="card-title">
                    <i className="bi bi-clock-history me-2"></i>
                    Update History
                  </h6>
                  <div className="text-center py-4">
                    <i className="bi bi-clock text-muted display-6 mb-3"></i>
                    <p className="text-muted mb-0">No update history available</p>
                    <small>History will appear after updates</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <style jsx>{`
        .card {
          border-radius: 15px;
          border: 1px solid rgba(0, 0, 0, 0.08);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
        }
        
        .card-title {
          color: #2c3e50;
          font-weight: 600;
        }
        
        .form-label {
          font-weight: 500;
          color: #495057;
        }
        
        .btn-primary {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          padding: 12px 24px;
          font-weight: 500;
          transition: all 0.3s ease;
        }
        
        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 20px rgba(102, 126, 234, 0.4);
        }
        
        .btn-outline-primary:hover {
          transform: translateY(-2px);
        }
        
        .list-group-item {
          background: transparent;
          border-color: rgba(0, 0, 0, 0.05);
        }
        
        .breadcrumb {
          background: transparent;
          padding: 0;
          margin-bottom: 1rem;
        }
        
        .breadcrumb-item a {
          color: #667eea;
          text-decoration: none;
        }
        
        .breadcrumb-item.active {
          color: #6c757d;
        }
        
        .pagetitle h1 {
          font-size: 2rem;
          font-weight: 700;
          color: #2c3e50;
          margin-bottom: 0.5rem;
        }
        
        .alert-info {
          background-color: rgba(13, 110, 253, 0.1);
          border-color: rgba(13, 110, 253, 0.2);
          color: #0a58ca;
        }
        
        .text-muted {
          color: #6c757d !important;
        }
        
        .badge {
          padding: 0.5em 0.8em;
          font-weight: 500;
        }
      `}</style>
    </>
  );
};

export default EditCategory;